// ============================================================
// placeUrl.ts — the URL form of a "place" (where the user is).
//
// A place is a ViewDescriptor (store): the screen + its ids + tab, plus a
// small `params` bag the mounted screen owns (an open bill, a section, a
// filter — see usePlaceParam). Every browser history entry carries its
// place in history.state, and the address bar shows it, so Back/Forward
// and a refresh both land on exactly the same place.
//
// The URL reuses the deep-link shapes the app already understands, so
// links from emails keep working unchanged and a refresh goes through the
// same cold-open code:
//   dashboard      /
//   caseList       ?view=cases&list=recent|all
//   caseDetail     ?caseId=<id>&section=<section>[&billId=…]
//   clientSearch   ?view=clients
//   clientDetail   ?client=<id>&tab=<tab>[&billId=…]
//   billing        ?view=billing[&billId=…&status=…]
//   tasks          ?view=tasks
//   calendar       ?view=calendar[&cal=…&date=…]
//   documentSearch ?view=documents
//   admin          ?view=admin[&section=…]
//   settings       ?view=settings[&section=…]
// Anything else in `params` is appended as-is. Ids are only ever looked
// up through the normal access-checked API calls, so a bad or foreign id
// falls back (the server enforces access; nothing is trusted from here).
// ============================================================
import type { ViewDescriptor } from '../store/useCasevoStore'

export type Place = ViewDescriptor

const VIEW_TO_SLUG: Partial<Record<Place['view'], string>> = {
  caseList: 'cases',
  clientSearch: 'clients',
  billing: 'billing',
  tasks: 'tasks',
  calendar: 'calendar',
  documentSearch: 'documents',
  admin: 'admin',
  settings: 'settings',
}
const SLUG_TO_VIEW: Record<string, Place['view']> = Object.fromEntries(
  Object.entries(VIEW_TO_SLUG).map(([view, slug]) => [slug, view as Place['view']]),
)

// Keys the URL itself uses — a screen param can never shadow them.
const RESERVED = new Set(['view', 'list', 'client', 'tab', 'caseId', 'taskId', 'day', 'token'])

// Place → '?…' (or '' for the dashboard). Stable order so equal places
// always give the same string (cheap change detection).
export const placeToSearch = (place: Place): string => {
  const q = new URLSearchParams()
  switch (place.view) {
    case 'dashboard':
      break
    case 'caseDetail':
      if (place.caseId) q.set('caseId', place.caseId)
      if (place.caseSection) q.set('section', place.caseSection)
      break
    case 'clientDetail':
      if (place.clientId) q.set('client', place.clientId)
      if (place.clientSection) q.set('tab', place.clientSection)
      break
    case 'caseList':
      q.set('view', 'cases')
      if (place.listTab) q.set('list', place.listTab)
      break
    default:
      q.set('view', VIEW_TO_SLUG[place.view] ?? place.view)
  }
  if (place.params) {
    for (const k of Object.keys(place.params).sort()) {
      if (!RESERVED.has(k) && !q.has(k)) q.set(k, place.params[k])
    }
  }
  const s = q.toString()
  return s ? `?${s}` : ''
}

const restParams = (q: URLSearchParams, used: string[]): Record<string, string> | undefined => {
  const params: Record<string, string> = {}
  q.forEach((v, k) => { if (!used.includes(k) && !RESERVED.has(k)) params[k] = v })
  return Object.keys(params).length ? params : undefined
}

// '?…' → place. (App.tsx cold-opens ?caseId / ?taskId / ?day / ?token with
// their existing handlers first; ?caseId is also understood here for a
// history entry that has no stored place.)
// Returns null when the URL names no place (→ the default landing view).
export const placeFromSearch = (search: string): Place | null => {
  const q = new URLSearchParams(search)
  const caseId = q.get('caseId')
  if (caseId) {
    const section = q.get('section')
    return { view: 'caseDetail', caseId, ...(section ? { caseSection: section as Place['caseSection'] } : {}), params: restParams(q, ['section']) }
  }
  const clientId = q.get('client')
  if (clientId) {
    return { view: 'clientDetail', clientId, ...(q.get('tab') ? { clientSection: q.get('tab') as string } : {}), params: restParams(q, []) }
  }
  const slug = q.get('view')
  const view = slug ? SLUG_TO_VIEW[slug] : undefined
  if (!view) return null
  if (view === 'caseList') {
    const list = q.get('list')
    // Its other params too (?status=open, ?limitation=missing — the daily
    // nudge's email link), so a cold open lands on the same filters.
    return { view, ...(list === 'all' || list === 'recent' ? { listTab: list } : {}), params: restParams(q, []) }
  }
  return { view, params: restParams(q, []) }
}

// Equality for places (used to skip redundant history writes).
export const samePlace = (a: Place | null | undefined, b: Place | null | undefined): boolean =>
  !!a && !!b && a.view === b.view && placeToSearch(a) === placeToSearch(b)
  && (a.quickViewCaseId ?? null) === (b.quickViewCaseId ?? null)
